import React, { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Copy, BookOpen, Clock, Users, BarChart3, ArrowDownToLine, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { ArticleKeyTakeaways } from '@/types/articles';

interface KeyTakeawaysDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: ArticleKeyTakeaways | null;
  onAddToArticle: (data: ArticleKeyTakeaways, placement: 'top' | 'bottom') => void;
  onRemoveFromArticle?: () => void;
  isAddedToArticle?: boolean;
}

const difficultyColor: Record<string, string> = {
  Beginner: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
  Intermediate: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  Advanced: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
};

export const KeyTakeawaysDrawer: React.FC<KeyTakeawaysDrawerProps> = ({ open, onOpenChange, data, onAddToArticle, onRemoveFromArticle, isAddedToArticle }) => {
  const [placement, setPlacement] = useState<'top' | 'bottom'>('top');

  if (!data) return null;

  const copyTakeaways = () => {
    const text = data.takeaways.map((t, i) => `${i + 1}. ${t.title}: ${t.description}`).join('\n');
    navigator.clipboard.writeText(text);
    toast.success('Takeaways copied to clipboard');
  };

  const handleAddToArticle = () => {
    onAddToArticle(data, placement);
    toast.success(`Key takeaways added to ${placement} of article`);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent side="right" className="w-[480px] sm:max-w-[480px] flex flex-col p-0 mr-12" style={{ zIndex: 60 }}>
        <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            Key Takeaways
          </SheetTitle>
          <SheetDescription>Auto-generated key takeaways from your article content</SheetDescription>
        </SheetHeader>

        <ScrollArea className="flex-1 overflow-y-auto">
          <div className="space-y-4 p-6">
            {/* Summary */}
            <div className="rounded-lg border p-3 space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Summary</h4>
              <p className="text-xs leading-relaxed text-foreground">{data.summary}</p>
              
              <div className="space-y-1.5 pt-2 border-t">
                <div className="flex items-center gap-2 text-xs">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">Time to read:</span>
                  <span className="font-medium">{data.readingTime}</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">Meant for:</span>
                  <span className="font-medium">{data.targetAudience}</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <BarChart3 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">Level:</span>
                  <Badge className={`text-[10px] ${difficultyColor[data.difficulty] || ''}`}>{data.difficulty}</Badge>
                </div>
              </div>
            </div>

            {/* Takeaways as bullet points */}
            <div className="rounded-lg border p-3">
              <h4 className="text-xs font-semibold mb-2">Takeaways</h4>
              <ul className="space-y-1.5">
                {data.takeaways.map((takeaway, i) => (
                  <li key={i} className="flex gap-2 text-xs">
                    <span className="text-primary font-medium shrink-0">•</span>
                    <div>
                      <span className="font-medium">{takeaway.title}</span>
                      <span className="text-muted-foreground"> — {takeaway.description}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Add to Article CTA */}
            <div className="rounded-lg border-2 border-dashed border-primary/30 p-3 space-y-3 bg-primary/5">
              <div>
                <h4 className="text-xs font-semibold flex items-center gap-2">
                  <ArrowDownToLine className="h-3.5 w-3.5" />
                  Add in Article
                </h4>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Insert a formatted key takeaways section into your article and save it to the database.
                </p>
              </div>

              <RadioGroup value={placement} onValueChange={(v) => setPlacement(v as 'top' | 'bottom')} className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="top" id="placement-top" />
                  <Label htmlFor="placement-top" className="text-xs cursor-pointer">Top</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="bottom" id="placement-bottom" />
                  <Label htmlFor="placement-bottom" className="text-xs cursor-pointer">Bottom</Label>
                </div>
              </RadioGroup>

              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddToArticle} className="gap-2 flex-1 text-xs">
                  <ArrowDownToLine className="h-3.5 w-3.5" />
                  Add to Article ({placement})
                </Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={copyTakeaways} title="Copy to clipboard">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
              {isAddedToArticle && onRemoveFromArticle && (
                <Button 
                  size="sm" 
                  variant="destructive" 
                  onClick={() => { onRemoveFromArticle(); toast.success('Key takeaways removed from article'); }}
                  className="gap-2 w-full text-xs"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Remove from Article
                </Button>
              )}
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
